/**
 * Zustand store slice — fact-checker state.
 *
 * Tracks the full lifecycle: idle → extracting → validating → done.
 * Individual claim cards are stored here and updated as validation
 * results arrive.
 */

import { create } from 'zustand';
import type { ClaimCard, ValidationSummary, Verdict } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Phase =
  | 'idle'          // nothing selected / run
  | 'extracting'    // waiting for /v1/extract to complete
  | 'validating'    // waiting for /v1/validate jobs to complete
  | 'done'          // all validation results received
  | 'error';        // unrecoverable error

interface FactCheckerState {
  // ── Selection ─────────────────────────────────────────────────────────────
  /** The text the user selected in Word. Null when nothing is selected. */
  selectedText: string | null;

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  phase: Phase;
  extractionJobId: string | null;
  validationJobId: string | null;
  globalError: string | null;

  /** Monotonic counter — stale async callbacks ignore results from older runs. */
  validationRunId: number;

  // ── Claims ────────────────────────────────────────────────────────────────
  /** One card per atomic claim extracted from the selected text. */
  claims: ClaimCard[];

  /** When set, only claim cards with this verdict are shown (counts stay total). */
  verdictFilter: Verdict | null;

  // ── Actions ───────────────────────────────────────────────────────────────
  setSelectedText: (text: string | null) => void;
  setPhase: (phase: Phase) => void;
  setExtractionJobId: (id: string | null) => void;
  setValidationJobId: (id: string | null) => void;
  setGlobalError: (error: string | null) => void;

  /** Replace the full claims list (called after extraction completes). */
  setClaims: (claims: ClaimCard[]) => void;

  /** Apply validation results for an entire run (single batched /v1/validate job). */
  resolveAllClaimValidations: (
    runId: number,
    results: Array<{
      position: number;
      verdict: Verdict;
      confidence: number | null;
      reasoning: string;
      supporting_chunks: ClaimCard['supporting_chunks'];
      contradicting_chunks: ClaimCard['contradicting_chunks'];
      source_types_used: string[];
    }>,
  ) => void;

  /** Merge newly completed validation results while a job is still processing. */
  mergeValidationResults: (
    runId: number,
    results: Array<{
      position: number;
      verdict: Verdict;
      confidence: number | null;
      reasoning: string;
      supporting_chunks: ClaimCard['supporting_chunks'];
      contradicting_chunks: ClaimCard['contradicting_chunks'];
      source_types_used: string[];
    }>,
  ) => void;

  /** Update a single claim's validation result (called as each job resolves). */
  resolveClaimValidation: (
    key: string,
    result: {
      verdict: Verdict;
      confidence: number | null;
      reasoning: string;
      supporting_chunks: ClaimCard['supporting_chunks'];
      contradicting_chunks: ClaimCard['contradicting_chunks'];
      source_types_used: string[];
    },
  ) => void;

  /** Mark a single claim's validation as errored. */
  rejectClaimValidation: (key: string, error: string) => void;

  /** Toggle verdict filter on the claim list (click same verdict again to clear). */
  toggleVerdictFilter: (verdict: Verdict) => void;

  /** Reset everything to the idle state. */
  reset: () => void;

  // ── Derived ───────────────────────────────────────────────────────────────
  summary: () => ValidationSummary;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useFactCheckerStore = create<FactCheckerState>((set, get) => ({
  selectedText: null,
  phase: 'idle',
  extractionJobId: null,
  validationJobId: null,
  globalError: null,
  validationRunId: 0,
  claims: [],
  verdictFilter: null,

  setSelectedText: (text) => set({ selectedText: text }),
  setPhase: (phase) => set({ phase }),
  setExtractionJobId: (id) => set({ extractionJobId: id }),
  setValidationJobId: (id) => set({ validationJobId: id }),
  setGlobalError: (error) => set({ globalError: error }),

  setClaims: (claims) => set({ claims }),

  resolveAllClaimValidations: (runId, results) =>
    set((state) => {
      if (runId !== state.validationRunId) return state;
      const byPosition = new Map(results.map((r) => [r.position, r]));
      return {
        claims: state.claims.map((claim, index) => {
          const result = byPosition.get(index);
          if (!result) {
            return {
              ...claim,
              validationStatus: 'error',
              error: 'No validation result returned for this claim',
            };
          }
          return {
            ...claim,
            validationStatus: 'done',
            verdict: result.verdict,
            confidence: result.confidence,
            reasoning: result.reasoning,
            supporting_chunks: result.supporting_chunks,
            contradicting_chunks: result.contradicting_chunks,
            source_types_used: result.source_types_used,
          };
        }),
      };
    }),

  mergeValidationResults: (runId, results) =>
    set((state) => {
      if (runId !== state.validationRunId) return state;
      const byPosition = new Map(results.map((r) => [r.position, r]));
      return {
        claims: state.claims.map((claim, index) => {
          const result = byPosition.get(index);
          if (!result || claim.validationStatus === 'done') {
            return claim;
          }
          return {
            ...claim,
            validationStatus: 'done',
            verdict: result.verdict,
            confidence: result.confidence,
            reasoning: result.reasoning,
            supporting_chunks: result.supporting_chunks,
            contradicting_chunks: result.contradicting_chunks,
            source_types_used: result.source_types_used,
          };
        }),
      };
    }),

  resolveClaimValidation: (key, result) =>
    set((state) => ({
      claims: state.claims.map((c) =>
        c.key === key
          ? {
              ...c,
              validationStatus: 'done',
              verdict: result.verdict,
              confidence: result.confidence,
              reasoning: result.reasoning,
              supporting_chunks: result.supporting_chunks,
              contradicting_chunks: result.contradicting_chunks,
              source_types_used: result.source_types_used,
            }
          : c,
      ),
    })),

  rejectClaimValidation: (key, error) =>
    set((state) => ({
      claims: state.claims.map((c) =>
        c.key === key
          ? { ...c, validationStatus: 'error', error }
          : c,
      ),
    })),

  toggleVerdictFilter: (verdict) =>
    set((state) => ({
      verdictFilter: state.verdictFilter === verdict ? null : verdict,
    })),

  reset: () =>
    set((state) => ({
      selectedText: null,
      phase: 'idle',
      extractionJobId: null,
      validationJobId: null,
      globalError: null,
      claims: [],
      verdictFilter: null,
      validationRunId: state.validationRunId + 1,
    })),

  summary: () => {
    const { claims } = get();
    const done = claims.filter((c) => c.validationStatus === 'done');
    return {
      total: done.length,
      supported: done.filter((c) => c.verdict === 'supported').length,
      unsupported: done.filter((c) => c.verdict === 'unsupported').length,
      contradicted: done.filter((c) => c.verdict === 'contradicted').length,
    };
  },
}));
